import { FC, useEffect, useState } from 'react';
import ExpandWrapper from 'components/ExpandWrapper/ExpandWrapper';
import Drawer from 'components/Drawer/Drawer';
import { IPractitioner } from 'interfaces/practitioners';
import { FormProvider, useForm } from 'react-hook-form';
import {
  NEXT_23_MONTH,
  SCHEDULE_TYPE,
  SERVICE_NAME,
  SPECIALTY_NAME,
  WORKING_HOUR_SYSTEM,
} from 'utils/constants';
import { Option } from '../../PractitionerProfile/MultipleSelect/MultipleSelect';
import ServiceDropdown from '../../PractitionerProfile/ServiceDropdown/ServiceDropdown';
import OperatoryDropdown from '../../PractitionerProfile/OperatoryDropdown/OperatoryDropdown';
import CommonButton from 'components/CommonButton/CommonButton';
import SpecialtyDropdown from '../../PractitionerProfile/SpecialtyDropdown/SpecialtyDropdown';
import CustomDaysList from '../CustomDaysList/CustomDaysList';
import DayTimeSlot from '../DayTimeSlot/DayTimeSlot';
import { Action, ScheduleNavigator } from 'hooks/useScheduleNavigator';
import moment from 'moment';
import useClinicSchedule from 'hooks/useClinicSchedule';

import { WeeklyTimeslot } from 'pages/OfficeSchedulePage/WrapperSchedulePage';
import { ClinicSchedule, WeekWorkingHour } from 'interfaces/clinicSchedule';
import normalizeWeeklySchedule from 'utils/normalizeWeeklySchedule';
import axiosInstance from 'apis/axiosInstance';
import { renderToast } from 'components/Toast/Toast';
import usePractitionerSettings from 'hooks/usePractitionerSettings';
import normalizeAdvancedWeeklySchedule from 'utils/normalizeAdvancedWeeklySchedule';
import useMatchMutate from 'hooks/useMatchMutate';
import { isEqual, keyBy, List } from 'lodash';
import { PractitionerSchedule as IPractitionerSchedule } from 'interfaces/practitionerSchedule';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import OverrideOperatory from './OverrideOperatory/OverrideOperatory';
import { PractitionerException } from 'interfaces/practitionerException';
import normalizeEventBreak, { LunchBreakItem } from 'utils/normalizeEventBreak';
import getPractitionerName from 'utils/getPractitionerName';
import PractitionerServicesOperatories from './PractitionerServicesOperatories/PractitionerServicesOperatories';
import { IService } from 'interfaces/services';
import useClinicService from 'hooks/useClinicService';

interface PractitionerScheduleProps {
  isShowing: boolean;
  onClose: () => void;
  selectedPractitioner: IPractitioner;
  state: ScheduleNavigator;
  dispatch: React.Dispatch<Action>;
  onCheckOutsidePractitionerHourAppts: () => Promise<void>;
  practitionerSchedule: IPractitionerSchedule;
}

export interface ServicesOperatories {
  serviceId: string;
  operatoryIds: string[];
  service: IService;
}

export interface PractitionerFormValue {
  isAdvancedSchedule: boolean;
  specialties: Option[];
  services: Option[];
  operatories: Option[];
  prevOperatories: Option[];
  weeklyTimeSlot: PractitionerWeeklyTimeSlot[];
  advancedWeeklyTimeSlot: PractitionerWeeklyTimeSlot[];
  weeklyTimeSlotLunchBreak: LunchBreakItem[];
  advancedWeeklyTimeSlotLunchBreak: LunchBreakItem[];
  servicesOperatories: ServicesOperatories[];
}

export interface PractitionerWeeklyTimeSlot extends WeeklyTimeslot {
  operatories?: Option[] | null;
  isAnimate?: boolean;
}

const PractitionerSchedule: FC<PractitionerScheduleProps> = ({
  isShowing,
  onClose,
  selectedPractitioner,
  state,
  dispatch,
  onCheckOutsidePractitionerHourAppts,
  practitionerSchedule,
}) => {
  const [isCustomDaysTab, setIsCustomDaysTab] = useState(false);

  const { data: clinicSchedule } = useClinicSchedule();

  const { data: practitionerSettings, mutate } = usePractitionerSettings(
    selectedPractitioner.id
  );

  const { data: clinicServices } = useClinicService();

  const matchMutate = useMatchMutate();

  const [isConfirmed, setIsConfirmed] = useState(false);

  const [isOverrideModal, setIsOverrideModal] = useState(false);

  const [exceptionDates, setExceptionDates] = useState<PractitionerException[]>(
    []
  );

  const getClinicSchedule = () => {
    if (!clinicSchedule) return [];

    const selectedSchedule =
      clinicSchedule.currentWorkingHour === WORKING_HOUR_SYSTEM.FIRSTIN
        ? SCHEDULE_TYPE.DEFAULT
        : SCHEDULE_TYPE.GOOGLE;

    const selectedSourceData = clinicSchedule[
      selectedSchedule as keyof ClinicSchedule
    ] as WeekWorkingHour;

    const timeSlots = normalizeWeeklySchedule(selectedSourceData);
    return timeSlots;
  };

  const methods = useForm<PractitionerFormValue>({
    mode: 'all',
    defaultValues: {
      isAdvancedSchedule: false,
      specialties: selectedPractitioner.specialties?.map((item) => {
        return { value: item.id, label: item.name };
      }),
      services: selectedPractitioner.services.reduce((prev, curr) => {
        if (curr.name === SERVICE_NAME.EXAM_CLEANING) {
          const childServices = curr.childServices
            .filter((item) => !item.isSplitSchedulingServiceByDentist)
            .map((item) => {
              return { value: item.id, label: item.name };
            });
          return [...prev, ...childServices];
        }
        const data = { value: curr.id, label: curr.name };
        return [...prev, data];
      }, [] as Option[]),
      operatories: selectedPractitioner.operatories.map((item) => {
        return { value: item.id, label: item.name };
      }),
      prevOperatories: selectedPractitioner.operatories.map((item) => {
        return { value: item.id, label: item.name };
      }),
      servicesOperatories: [],
      weeklyTimeSlot: [],
      advancedWeeklyTimeSlot: [],
      weeklyTimeSlotLunchBreak: [],
      advancedWeeklyTimeSlotLunchBreak: [],
    },
  });

  const { control, watch, setValue, reset, formState, handleSubmit } = methods;

  const {
    isAdvancedSchedule,
    advancedWeeklyTimeSlot,
    services,
    specialties,
    prevOperatories,
    servicesOperatories,
    operatories,
  } = watch();

  useEffect(() => {
    if (!practitionerSettings) return;

    const schedule = normalizeWeeklySchedule(practitionerSettings.schedule);

    const advancedSchedule = normalizeAdvancedWeeklySchedule(
      practitionerSettings.advancedSchedule,
      practitionerSettings.operatories
    );

    const weeklyTimeSlotLunchBreak = normalizeEventBreak(
      practitionerSettings.schedule.lunchBreaks
    );

    const advancedWeeklyTimeSlotLunchBreak = normalizeEventBreak(
      practitionerSettings.advancedSchedule.lunchBreaks
    );

    const isFirstTimeAdvancedSetup =
      !practitionerSettings.isAdvancedSchedule &&
      advancedSchedule.every((item) => !item.start && !item.end);

    let servicesOperatories = practitionerSettings.servicesOperatories;

    if (servicesOperatories.length === 0) {
      servicesOperatories =
        practitionerSettings.services?.map((service: any) => ({
          serviceId: service.id,
          operatoryIds:
            practitionerSettings.operatories?.map(
              (operatory: any) => operatory.id
            ) ?? [],
          service: service,
        })) ?? [];
    }

    reset({
      ...watch(),
      isAdvancedSchedule: practitionerSettings.isAdvancedSchedule,
      weeklyTimeSlot: schedule,
      advancedWeeklyTimeSlot: isFirstTimeAdvancedSetup
        ? schedule
        : advancedSchedule,
      weeklyTimeSlotLunchBreak,
      advancedWeeklyTimeSlotLunchBreak,
      servicesOperatories,
    });
  }, [practitionerSettings, reset, watch]);

  const isAdvanedSetupValid = advancedWeeklyTimeSlot.every((timeSlot) => {
    const isWorkingDay = timeSlot.day && timeSlot.end;
    if (!isWorkingDay) return true;
    return timeSlot.operatories && timeSlot.operatories.length > 0;
  });

  const isFormValid =
    formState.isValid && (isAdvancedSchedule ? isAdvanedSetupValid : true);

  const isFormChanged =
    formState.dirtyFields.operatories ||
    formState.dirtyFields.services ||
    formState.dirtyFields.specialties ||
    formState.dirtyFields.servicesOperatories ||
    formState.dirtyFields.isAdvancedSchedule ||
    (isAdvancedSchedule
      ? formState.dirtyFields.advancedWeeklyTimeSlot
      : formState.dirtyFields.weeklyTimeSlot) ||
    (isAdvancedSchedule
      ? formState.dirtyFields.advancedWeeklyTimeSlotLunchBreak
      : formState.dirtyFields.weeklyTimeSlotLunchBreak);

  const onOperatoryUpdated = (value: { value: string; label: string }[]) => {
    const isAdded = value.length > operatories.length;

    if (isAdded) {
      const newlyAddedOperatory = value.find(
        (item) => !operatories.find((o) => o.value === item.value)
      );

      setValue(
        'servicesOperatories',
        [...servicesOperatories].map((item) => ({
          ...item,
          operatoryIds: [...item.operatoryIds, newlyAddedOperatory!.value],
        })),
        { shouldValidate: true, shouldDirty: true }
      );
    } else {
      const removedOperatory = operatories.find(
        (item) => !value.find((v) => v.value === item.value)
      );
      setValue(
        'servicesOperatories',
        [...servicesOperatories].map((item) => ({
          ...item,
          operatoryIds: item.operatoryIds.filter(
            (id) => id !== removedOperatory!.value
          ),
        })),
        { shouldValidate: true, shouldDirty: true }
      );
    }

    if (!isAdvancedSchedule) return;

    const updatedWeeklyTimeSlots = advancedWeeklyTimeSlot.map((item) => ({
      ...item,
    }));

    const isOperatoriesRemove = prevOperatories.length > value.length;

    const foundOperatoriesRemoved = isOperatoriesRemove
      ? prevOperatories.find(
          (item) => !value.find((v) => v.value === item.value)
        )
      : null;

    updatedWeeklyTimeSlots.forEach((item) => {
      if (item.operatories) {
        item.isAnimate = foundOperatoriesRemoved
          ? item.operatories.some(
              (operatory) => operatory.value === foundOperatoriesRemoved.value
            )
          : false;

        item.operatories = foundOperatoriesRemoved
          ? item.operatories.filter(
              (operatory) => operatory.value !== foundOperatoriesRemoved.value
            )
          : item.operatories;
      }
    });

    setValue('prevOperatories', value, {
      shouldValidate: true,
    });
    setValue('advancedWeeklyTimeSlot', updatedWeeklyTimeSlots, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onServiceUpdated = (value: { value: string; label: string }[]) => {
    const isAdded = value.length > services.length;
    if (isAdded) {
      const newlyAddedService = value.find(
        (item) => !services.find((s) => s.value === item.value)
      )!;

      let updatedServicesOperatories = [...servicesOperatories];

      const isNewlyExamCleaning = newlyAddedService.label.includes(
        SERVICE_NAME.EXAM_CLEANING
      );

      if (isNewlyExamCleaning) {
        const parentExamCleaning = clinicServices?.data.find(
          (item) => item.name === SERVICE_NAME.EXAM_CLEANING
        );

        const isExamCleaningAlreadyAdded = servicesOperatories.some(
          (item) => item.service.name === SERVICE_NAME.EXAM_CLEANING
        );

        if (isExamCleaningAlreadyAdded) return;

        updatedServicesOperatories = [
          ...updatedServicesOperatories,
          {
            serviceId: parentExamCleaning!.id,
            operatoryIds: operatories.map((item) => item.value),
            service: parentExamCleaning!,
          },
        ];
      } else {
        updatedServicesOperatories = [
          ...updatedServicesOperatories,
          {
            serviceId: newlyAddedService.value,
            operatoryIds: operatories.map((item) => item.value),
            service: clinicServices?.data.find(
              (s) => s.id === newlyAddedService!.value
            )!,
          },
        ];
      }

      setValue('servicesOperatories', updatedServicesOperatories, {
        shouldValidate: true,
        shouldDirty: true,
      });

      return;
    }

    let updatedServicesOperatories = [...servicesOperatories];

    const isAllExamCleainingRemoved = value.every(
      (item) => !item.label.includes(SERVICE_NAME.EXAM_CLEANING)
    );

    const removedService = services.find(
      (item) => !value.find((v) => v.value === item.value)
    )!;

    const isRemovedServiceExamCleaning = removedService.label.includes(
      SERVICE_NAME.EXAM_CLEANING
    );

    if (isAllExamCleainingRemoved && isRemovedServiceExamCleaning) {
      updatedServicesOperatories = updatedServicesOperatories.filter(
        (item) => item.service.name !== SERVICE_NAME.EXAM_CLEANING
      );
    } else {
      const removedService = services.find(
        (item) => !value.find((v) => v.value === item.value)
      );
      updatedServicesOperatories = updatedServicesOperatories.filter(
        (item) => item.serviceId !== removedService!.value
      );
    }

    setValue('servicesOperatories', updatedServicesOperatories, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const onSpecialtyUpdated = (newValue: Option[]) => {
    if (!clinicServices) return;
    const GENERAL_DENTIST_LABEL = 'General Dentist';

    const isHygienistRemoved =
      !newValue.some((item) => item.label === SPECIALTY_NAME.HYGIENIST) &&
      specialties.some((item) => item.label === SPECIALTY_NAME.HYGIENIST);

    const isHygienistAdded =
      newValue.some((item) => item.label === SPECIALTY_NAME.HYGIENIST) &&
      !specialties.some((item) => item.label === SPECIALTY_NAME.HYGIENIST);

    const isDentistAdded =
      newValue.some((item) => item.label === GENERAL_DENTIST_LABEL) &&
      !specialties.some((item) => item.label === GENERAL_DENTIST_LABEL);

    const isDentistRemoved =
      !newValue.some((item) => item.label === GENERAL_DENTIST_LABEL) &&
      specialties.some((item) => item.label === GENERAL_DENTIST_LABEL);

    if (isHygienistRemoved) {
      const updatedServicesOperatories = servicesOperatories.filter(
        (item) => item.service.name !== SERVICE_NAME.EXAM_CLEANING
      );

      setValue('servicesOperatories', updatedServicesOperatories, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (isDentistRemoved) {
      const updatedServicesOperatories = servicesOperatories.filter(
        (item) => item.service.name === SERVICE_NAME.EXAM_CLEANING
      );

      const updatedServices = services.filter((item) =>
        item.label.includes(SERVICE_NAME.EXAM_CLEANING)
      );

      setValue('servicesOperatories', updatedServicesOperatories, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue('services', updatedServices, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (isHygienistAdded) {
      const examCleaningServices = clinicServices.data.find(
        (service) =>
          service.name === SERVICE_NAME.EXAM_CLEANING && service.isActive
      );

      const updatedServices =
        examCleaningServices?.childServices.map((service) => ({
          label: service.name,
          value: service.id,
        })) ?? [];

      setValue('services', [...services, ...updatedServices], {
        shouldValidate: true,
        shouldDirty: true,
      });

      const isExamCleaningAlreadyAdded = servicesOperatories.some(
        (item) => item.service.name === SERVICE_NAME.EXAM_CLEANING
      );

      if (!isExamCleaningAlreadyAdded) {
        setValue(
          'servicesOperatories',
          [
            ...servicesOperatories,
            {
              operatoryIds: operatories.map((item) => item.value),
              serviceId: examCleaningServices!.id,
              service: examCleaningServices!,
            },
          ],
          { shouldValidate: true, shouldDirty: true }
        );
      }
    }

    if (isDentistAdded) {
      const otherServices = clinicServices.data.filter(
        (service) =>
          !service.name.includes(SERVICE_NAME.EXAM_CLEANING) && service.isActive
      );

      const data = otherServices.map((service) => ({
        label: service.name,
        value: service.id,
      }));

      const updatedServices = data.reduce((prev, curr) => {
        const isServicesExisted = services.find(
          (item) => item.value === curr.value
        );
        if (isServicesExisted) {
          return [...prev];
        }

        return [...prev, curr];
      }, services);

      setValue('services', updatedServices, {
        shouldValidate: true,
        shouldDirty: true,
      });

      const otherNonSelectServices = data.filter(
        (service) =>
          !servicesOperatories.find((item) => item.serviceId === service.value)
      );

      const updatedServicesOperatories = [
        ...servicesOperatories,
        ...otherNonSelectServices.map((service) => ({
          serviceId: service.value,
          operatoryIds: operatories.map((item) => item.value),
          service: clinicServices.data.find((s) => s.id === service.value)!,
        })),
      ];

      setValue('servicesOperatories', updatedServicesOperatories, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  const isDataSame = (
    defaultValues: List<unknown> | null | undefined,
    updatedValue: List<unknown> | null | undefined
  ) => {
    return isEqual(keyBy(defaultValues, 'value'), keyBy(updatedValue, 'value'));
  };

  const onSubmit = async (data: PractitionerFormValue) => {
    setIsConfirmed(false);
    const scheduleId = data.isAdvancedSchedule
      ? practitionerSettings!.advancedSchedule.id
      : practitionerSettings!.schedule.id;

    const payload: { [key: string]: any } = {
      isAdvancedSchedule: data.isAdvancedSchedule,
      schedules: {
        id: scheduleId,
      },
    };

    const { services, weeklyTimeSlot, isAdvancedSchedule } =
      formState.defaultValues!;

    const lunchBreaks = data.isAdvancedSchedule
      ? data.advancedWeeklyTimeSlotLunchBreak
      : data.weeklyTimeSlotLunchBreak;

    const lunchBreaksPayload = lunchBreaks.reduce(
      (prev, curr: LunchBreakItem) => {
        const isLunchBreakValid = curr.startFirst && curr.endFirst;
        if (!isLunchBreakValid) return prev;
        const item = {
          [curr.day]: [
            {
              start: curr.startFirst,
              end: curr.endFirst,
            },
          ],
        };

        if (
          curr.startSecond &&
          curr.endSecond &&
          curr.startSecond !== curr.endSecond
        ) {
          item[curr.day].push({
            start: curr.startSecond,
            end: curr.endSecond,
          });
        }

        return { ...prev, ...item };
      },
      {}
    );

    payload.lunchBreaks = lunchBreaksPayload;
    payload.lunchBreaks.doctorScheduleId = scheduleId;

    const isServicesChanged = formState.dirtyFields.services;

    const isSpecialtiesChanged = formState.dirtyFields.specialties;

    const isServicesOperatoriesChanged =
      formState.dirtyFields.servicesOperatories;

    payload.operatoryIds = data.operatories.map((item) => item.value);

    if (isServicesChanged) {
      payload.serviceIds = data.services.map((item) => item.value);

      const isExamCleaningServiceAdded = data.services.some((item) =>
        item.label.includes(SERVICE_NAME.EXAM_CLEANING)
      );

      const isExamCleaningServiceRemoved =
        services?.some((service) =>
          service?.label?.includes(SERVICE_NAME.EXAM_CLEANING)
        ) &&
        !data.services.some(
          (item) => item.label === SERVICE_NAME.EXAM_CLEANING
        );

      if (isExamCleaningServiceAdded || isExamCleaningServiceRemoved) {
        payload.isExamCleaningModified = true;
      }
    }

    if (isSpecialtiesChanged) {
      payload.specialtyIds = data.specialties.map((item) => item.value);
    }

    if (isServicesOperatoriesChanged || isServicesChanged) {
      let servicesOperatoriesPayload = data.servicesOperatories.map((item) => ({
        serviceId: item.serviceId,
        operatoryIds: item.operatoryIds,
      }));

      const parentExamCleaning = data.servicesOperatories.find(
        (item) => item.service.name === SERVICE_NAME.EXAM_CLEANING
      );

      if (parentExamCleaning) {
        const childServices = data.services.filter((service) =>
          service.label.includes(SERVICE_NAME.EXAM_CLEANING)
        );
        servicesOperatoriesPayload = [
          ...servicesOperatoriesPayload,
          ...childServices.map((service) => ({
            serviceId: service.value,
            operatoryIds: parentExamCleaning.operatoryIds,
          })),
        ];
      }

      payload.servicesOperatories = servicesOperatoriesPayload;
    }

    if (data.isAdvancedSchedule) {
      data.advancedWeeklyTimeSlot.forEach((item, index) => {
        const key = item.day;

        payload.schedules[key] = [
          {
            start: item.start,
            end: item.end,
            operatoryIds:
              item.start && item.end
                ? item.operatories!.map((item) => item.value)
                : null,
          },
        ];
      });
    } else {
      data.weeklyTimeSlot.forEach((item, index) => {
        const key = item.day;

        const defaultWeeklyTimeSlot = weeklyTimeSlot![index]!;

        const isWeeklyTimeSlotChanged =
          defaultWeeklyTimeSlot.start !== item.start ||
          defaultWeeklyTimeSlot.end !== item.end;

        if (isWeeklyTimeSlotChanged) {
          payload.schedules[key] = [
            {
              start: item.start,
              end: item.end,
            },
          ];
        }
      });
    }

    payload.isModeChanged = data.isAdvancedSchedule !== isAdvancedSchedule;

    try {
      await axiosInstance.patch(
        `practitioners/${selectedPractitioner.id}/advanced-schedule`,
        payload
      );
      await Promise.all([
        mutate(practitionerSettings, { revalidate: true }),
        matchMutate(/exceptions\?[\s\S]+/),
        matchMutate(/practitioners\?[\s\S]+/),
      ]);
      renderToast({
        type: 'success',
        message: 'Successfully updated practitioner settings',
      });
      onClose();
    } catch (error) {
      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  const onSave = async () => {
    const { isAdvancedSchedule, operatories } = watch();
    const isUpdatedFromAdvanedModeToNormalMode =
      practitionerSettings?.isAdvancedSchedule && !isAdvancedSchedule;

    if (isUpdatedFromAdvanedModeToNormalMode) {
      return setIsConfirmed(true);
    }

    if (isAdvancedSchedule) {
      const isOperatoriesChanged = !isDataSame(
        formState.defaultValues?.operatories,
        operatories
      );

      const query = new URLSearchParams({
        startDate: moment().format('YYYY-MM-DD'),
        endDate: moment().add(NEXT_23_MONTH, 'months').format('YYYY-MM-DD'),
      }).toString();

      try {
        const data = await axiosInstance.get(
          `/practitioners/${selectedPractitioner.id}/custom-days?${query}`
        );

        const exceptionDates = data.data as PractitionerException[];

        const modifiedExceptionDatesOperatories = exceptionDates.filter(
          (exception) => {
            if (exception.isClosed) {
              return false;
            }
            const currentExceptionOperatories = exception.operatories;

            const isOperatoriesRemoved = currentExceptionOperatories.some(
              (operatory) =>
                !operatories.find((item) => item.value === operatory.id)
            );

            return isOperatoriesRemoved;
          }
        );

        if (
          modifiedExceptionDatesOperatories.length > 0 &&
          isOperatoriesChanged
        ) {
          setIsOverrideModal(true);
          setExceptionDates(modifiedExceptionDatesOperatories);
          return;
        }
      } catch (error) {
        renderToast({
          type: 'error',
          message: 'Something went wrong. Please try again later',
        });
        return;
      }
    }

    handleSubmit(onSubmit)();
  };

  return (
    <>
      {isConfirmed && (
        <ConfirmationModal
          title={'Update Settings'}
          description={`Updating these settings will reset this practitioner's schedules to their default ones. Do you wish to proceed?`}
          onClose={() => setIsConfirmed(false)}
          onSubmit={async () => {
            await handleSubmit(onSubmit)();
          }}
          submitBtnTitle="Confirm"
        />
      )}
      {isOverrideModal && (
        <OverrideOperatory
          onConfirm={() => {
            setIsOverrideModal(false);
            setExceptionDates([]);
            handleSubmit(onSubmit)();
          }}
          onCancel={() => setIsOverrideModal(false)}
          exceptionDates={exceptionDates}
        />
      )}

      <FormProvider {...methods}>
        <Drawer
          isShowing={isShowing}
          setIsShowing={onClose}
          title={`${getPractitionerName(selectedPractitioner)} Settings`}
          footer={
            <div className="w-full h-6 flex px-2.4 justify-end items-center shadow-primary sticky bottom-0 bg-white">
              <CommonButton
                className="!min-w-[12rem]"
                disabled={!isFormValid || !isFormChanged}
                isLoading={formState.isSubmitting}
                onClick={onSave}
              >
                Save
              </CommonButton>
            </div>
          }
          onReset={() => reset()}
        >
          <div className="flex flex-col gap-y-3">
            <ExpandWrapper
              header={
                <span className="font-bold text-16 leading-[2.4rem] text-darkest-grey">
                  Settings
                </span>
              }
            >
              <div className="pt-2 flex flex-col gap-y-2">
                <div className="flex flex-col gap-y-1">
                  <span className="font-bold text-16 leading-[2.4rem] text-darkest-grey">
                    Practitioner Specialties
                  </span>
                  <SpecialtyDropdown
                    rules={{ required: 'This field is required' }}
                    controlStyles={{ height: '4rem' }}
                    control={control}
                    services={services}
                    setValue={setValue}
                    onUpdated={onSpecialtyUpdated}
                  />
                </div>

                <div className="flex flex-col gap-y-1">
                  <span className="font-bold text-16 leading-[2.4rem] text-darkest-grey">
                    Practitioner Services
                  </span>
                  <ServiceDropdown
                    className="!h-4 !pr-0.5"
                    control={control}
                    specialties={specialties}
                    onChange={onServiceUpdated}
                  />
                </div>

                <div className="flex flex-col gap-y-1">
                  <span className="font-bold text-16 leading-[2.4rem] text-darkest-grey">
                    Practitioner Operatories
                  </span>
                  <OperatoryDropdown
                    controlStyles={{ height: '4rem' }}
                    control={control}
                    onUpdate={onOperatoryUpdated}
                  />
                </div>
                <div className="flex flex-col gap-y-1">
                  <span className="font-bold text-16 leading-[2.4rem] text-darkest-grey">
                    Services by Operatories
                  </span>
                  <PractitionerServicesOperatories
                    control={control}
                    services={watch('services')}
                    operatories={watch('operatories')}
                  />
                </div>
              </div>
            </ExpandWrapper>
            <ExpandWrapper
              header={
                <span className="font-bold text-16 leading-[2.4rem] text-darkest-grey">
                  Schedule
                </span>
              }
            >
              <div className="-mx-3 pt-1 -mb-2.1">
                <div className="w-full">
                  <div className="h-4.8 flex justify-evenly items-center border border-light-grey">
                    <button
                      className={`basis-[13rem] ${
                        !isCustomDaysTab ? 'text-magenta font-bold' : ''
                      }`}
                      onClick={() => setIsCustomDaysTab(false)}
                    >
                      Regular Hours
                    </button>
                    <span className="h-3 border-l border-light-grey"></span>
                    <button
                      className={`basis-[13rem] ${
                        isCustomDaysTab ? 'text-magenta font-bold' : ''
                      }`}
                      onClick={() => setIsCustomDaysTab(true)}
                    >
                      Custom Days
                    </button>
                  </div>
                </div>
                {isCustomDaysTab ? (
                  <CustomDaysList
                    selectedPractitioner={selectedPractitioner}
                    setDate={(date) =>
                      dispatch({
                        type: 'customDayNavigation',
                        payload: { monthValue: date },
                      })
                    }
                    isNavigatedByCalendar={
                      !state.highlightedDate && state.isNavigatedByCalendar
                    }
                    monthValue={moment(state.monthValue, 'YYYY-MM-DD').format(
                      'MMMM YYYY'
                    )}
                    onCheckOutsidePractitionerHourAppts={
                      onCheckOutsidePractitionerHourAppts
                    }
                    version={clinicSchedule!.version}
                    practitionerSchedule={practitionerSchedule}
                  />
                ) : (
                  <DayTimeSlot
                    clinicTimeSlots={getClinicSchedule()}
                    cachedTimeSlots={
                      isAdvancedSchedule
                        ? (formState.defaultValues
                            ?.advancedWeeklyTimeSlot as PractitionerWeeklyTimeSlot[])
                        : (formState.defaultValues
                            ?.weeklyTimeSlot as PractitionerWeeklyTimeSlot[])
                    }
                    practitionerScheduleId={
                      practitionerSettings?.schedule.id || ''
                    }
                    selectedPractitioner={selectedPractitioner}
                    version={clinicSchedule!.version}
                    onCheckOutsidePractitionerHourAppts={
                      onCheckOutsidePractitionerHourAppts
                    }
                  />
                )}
              </div>
            </ExpandWrapper>
          </div>
        </Drawer>
      </FormProvider>
    </>
  );
};

export default PractitionerSchedule;
